import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { api } from '../api';

function SellerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedSeller = location.state?.seller || null;
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('');
  const [seller, setSeller] = useState(passedSeller || null);
  const [loading, setLoading] = useState(!passedSeller);
  const [grouped, setGrouped] = useState(null);
  const [walletTotal, setWalletTotal] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  const sellers = useMemo(
    () => ({
      1: { id: 1, name: 'Aurora Mart', email: 'contact@auroramart.com', phone: '+1-256-325-8652', image: '/assets/img/vendor/u3.jpg', sold: 12161, wishlist: 320, following: 450 },
      2: { id: 2, name: 'Bolbi Stores', email: 'info@bolbi.io', phone: '+5-256-325-8652', image: '/assets/img/vendor/u2.jpg', sold: 95161, wishlist: 210, following: 980 },
      3: { id: 3, name: 'Marlo Traders', email: 'hello@marlo.tr', phone: '+91-154-325-8652', image: '/assets/img/vendor/u4.jpg', sold: 21561, wishlist: 120, following: 600 },
    }),
    []
  );

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!passedSeller && id) {
        const res = await api.getSellerById(id);
        if (!isMounted) return;
        if (res?.ok) setSeller(res.data);
        setLoading(false);
      }
      const sellerId = passedSeller?.id || id;
      if (sellerId) {
        try {
          const [g, w, e] = await Promise.all([
            api.getSellerProductsGrouped(sellerId),
            api.getWalletTotal({ sellerId }),
            api.getEarnings({ sellerId }),
          ]);
          if (!isMounted) return;
          setGrouped(g);
          setWalletTotal(typeof w === 'number' ? w : null);
          setEarnings(typeof e === 'number' ? e : null);
        } catch {}
      }
    })();
    return () => { isMounted = false; };
  }, [id, passedSeller]);

  const user = seller || sellers[id] || sellers[1];
  const isActive = (user?.status || '').toUpperCase() === 'ACTIVE';
  const displayImage = user?.image?.url || user?.image || user?.imageUrl || '/assets/img/vendor/u9.jpg';
  const soldCount = typeof user?.sold === 'number' ? user.sold : 0;
  const wishlistCount = typeof user?.wishlist === 'number' ? user.wishlist : 0;
  const followingCount = typeof user?.following === 'number' ? user.following : 0;

  function formatDateTimeReadable(value) {
    if (!value || typeof value !== 'string') return '-';
    const sanitized = value.replace(' ', 'T');
    const date = new Date(sanitized);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  async function handleActivateSeller(e) {
    e.preventDefault();
    if (!user?.email) return;
    setActionLoading(true);
    setActionError('');
    const payload = {
      firstName: user.name?.split(' ')[0] || 'First',
      lastName: user.name?.split(' ').slice(1).join(' ') || 'Last',
      businessMcc: '5411',
      dob: '1990-08-10T22:53:44.375Z',
    };
    const res = await api.activateSeller(user.email, payload);
    setActionLoading(false);
    if (res?.ok) {
      alert('Seller activation initiated successfully');
    } else {
      const msg = res?.message || 'Failed to activate seller';
      setActionError(msg);
      alert(msg);
    }
  }

  async function handleUpdateStatus(e) {
    e.preventDefault();
    if (!user?.id || !statusUpdate) return;
    setActionLoading(true);
    setActionError('');
    const res = await api.updateSellerStatus(user.id, statusUpdate);
    setActionLoading(false);
    if (res?.ok) {
      alert('Seller status updated');
      // naive refresh
      navigate(0);
    } else {
      const msg = res?.message || 'Failed to update status';
      setActionError(msg);
      alert(msg);
    }
  }

  return (
    <>
      <Helmet>
        <title>{user ? `${user.name} | Seller` : 'Seller Profile'} | Owena Admin Dashboard</title>
      </Helmet>
      <div className="ec-content-wrapper">
        <div className="content">
          <div className="breadcrumb-wrapper breadcrumb-contacts">
            <div>
              <h1>User Profile</h1>
              <p className="breadcrumbs"><span><a href="#" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>Home</a></span>
                <span><i className="mdi mdi-chevron-right"></i></span>Profile</p>
            </div>
            <div className="d-flex flex-wrap align-items-stretch gap-3 justify-content-end">
              {!isActive && (
                <button className="btn btn-outline-primary d-flex align-items-center me-3" onClick={handleActivateSeller} disabled={actionLoading}>
                  {actionLoading ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : null}
                  Activate Seller
                </button>
              )}
              <div className="input-group" style={{ width: '340px' }}>
                <span className="input-group-text">Status</span>
                <select className="form-select" value={statusUpdate} onChange={e => setStatusUpdate(e.target.value)}>
                  <option value="">Choose...</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="FREEZE">FREEZE</option>
                  <option value="PENDING">PENDING</option>
                  <option value="SUSPENDED">SUSPENDED</option>
                </select>
                <button className="btn btn-success" type="button" onClick={handleUpdateStatus} disabled={actionLoading || !statusUpdate}>Update</button>
              </div>
            </div>
            {actionError ? <div className="alert alert-danger mt-3 mb-0" role="alert">{actionError}</div> : null}
          </div>
          <div className="card bg-white profile-content">
            <div className="row">
              <div className="col-lg-4 col-xl-3">
                <div className="profile-content-left profile-left-spacing">
                  <div className="text-center widget-profile px-0 border-0">
                    <div className="card-img mx-auto rounded-circle">
                      <img src={displayImage} alt="user image" />
                    </div>
                    <div className="card-body">
                      <h4 className="py-2 text-dark">{user.name}</h4>
                      <p>{user.email}</p>
                      <a className="btn btn-primary my-3" href="#" onClick={(e) => e.preventDefault()}>Follow</a>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between ">
                    <div className="text-center pb-4">
                      <h6 className="text-dark pb-2">{soldCount}</h6>
                      <p>Sold</p>
                    </div>
                    <div className="text-center pb-4">
                      <h6 className="text-dark pb-2">{wishlistCount}</h6>
                      <p>Wish List</p>
                    </div>
                    <div className="text-center pb-4">
                      <h6 className="text-dark pb-2">{followingCount}</h6>
                      <p>Following</p>
                    </div>
                  </div>
                  <hr className="w-100" />
                  <div className="contact-info pt-4">
                    <h5 className="text-dark">Contact Information</h5>
                    <p className="text-dark font-weight-medium pt-24px mb-2">Email address</p>
                    <p>{user.email}</p>
                    <p className="text-dark font-weight-medium pt-24px mb-2">Phone Number</p>
                    <p>{user.phone}</p>
                    <p className="text-dark font-weight-medium pt-24px mb-2">Birthday</p>
                    <p>Dec 10, 1991</p>
                    <p className="text-dark font-weight-medium pt-24px mb-2">Social Profile</p>
                    <p className="social-button">
                      <a href="#" className="mb-1 btn btn-outline btn-twitter rounded-circle" onClick={(e) => e.preventDefault()}><i className="mdi mdi-twitter"></i></a>
                      <a href="#" className="mb-1 btn btn-outline btn-linkedin rounded-circle" onClick={(e) => e.preventDefault()}><i className="mdi mdi-linkedin"></i></a>
                      <a href="#" className="mb-1 btn btn-outline btn-facebook rounded-circle" onClick={(e) => e.preventDefault()}><i className="mdi mdi-facebook"></i></a>
                      <a href="#" className="mb-1 btn btn-outline btn-skype rounded-circle" onClick={(e) => e.preventDefault()}><i className="mdi mdi-skype"></i></a>
                    </p>

                    <hr className="w-100" />
                    <h5 className="text-dark">Address</h5>
                    {user?.address ? (
                      <div>
                        <div className="mb-2"><strong>Line 1:</strong> {user.address?.data?.line1 || '-'}</div>
                        <div className="mb-2"><strong>Line 2:</strong> {user.address?.data?.line2 || '-'}</div>
                        <div className="mb-2"><strong>City:</strong> {user.address?.data?.postTown || '-'}</div>
                        <div className="mb-2"><strong>County:</strong> {user.address?.data?.county || '-'}</div>
                        <div className="mb-2"><strong>Postcode:</strong> {user.address?.data?.postCode || user.address?.postalCode || '-'}</div>
                        <div className="mb-2"><strong>Type:</strong> {user.address?.data?.postCodeType || '-'}</div>
                        <div className="mb-2"><strong>Comment:</strong> {user.address?.data?.comment || '-'}</div>
                        <div className="mb-2"><strong>Coordinates:</strong> {Array.isArray(user.address?.location?.coordinates) ? `${user.address.location.coordinates[1]}, ${user.address.location.coordinates[0]}` : '-'}</div>
                      </div>
                    ) : (
                      <p className="mb-2">-</p>
                    )}

                    <hr className="w-100" />
                    <h5 className="text-dark">Communities</h5>
                    {Array.isArray(user?.communities) && user.communities.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {user.communities.map((c) => (
                          <span key={c.id} className="badge bg-light text-dark d-flex align-items-center" style={{ border: '1px solid #ddd' }}>
                            {c.icon ? <img src={c.icon} alt={c.name} style={{ width: 18, height: 18, objectFit: 'cover', marginRight: 6 }} /> : null}
                            {c.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mb-2">-</p>
                    )}

                    <hr className="w-100" />
                    <h5 className="text-dark">Availability</h5>
                    {Array.isArray(user?.availability) && user.availability.length > 0 ? (
                      <div>
                        {user.availability.map((a, idx) => (
                          <div key={idx} className="d-flex justify-content-between mb-1">
                            <span><strong>{a.day}:</strong> {a.isOn ? 'On' : 'Off'}</span>
                            <span>{formatDateTimeReadable(a.openingTime)} - {formatDateTimeReadable(a.closingTime)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mb-0">-</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-8 col-xl-9">
                <div className="profile-content-right profile-right-spacing py-5">
                  <ul className="nav nav-tabs px-3 px-xl-5 nav-style-border" id="myProfileTab" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`} id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile" type="button" role="tab" aria-controls="profile" aria-selected={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>Profile</button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button className={`nav-link ${activeTab === 'products' ? 'active' : ''}`} id="products-tab" data-bs-toggle="tab" data-bs-target="#products" type="button" role="tab" aria-controls="products" aria-selected={activeTab === 'products'} onClick={() => setActiveTab('products')}>Products</button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`} id="settings-tab" data-bs-toggle="tab" data-bs-target="#settings" type="button" role="tab" aria-controls="settings" aria-selected={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>Settings</button>
                    </li>
                  </ul>
                  <div className="tab-content px-3 px-xl-5" id="myTabContent">
                    <div className={`tab-pane fade ${activeTab === 'profile' ? 'show active' : ''}`} id="profile" role="tabpanel" aria-labelledby="profile-tab">
                      <div className="tab-widget mt-5">
                        <div className="row">
                          <div className="col-xl-4">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle mr-3 bg-primary"><i className="mdi mdi-account-outline text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">{soldCount}</h4><p>Sold</p></div>
                            </div>
                          </div>
                          <div className="col-xl-4">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle bg-warning mr-3"><i className="mdi mdi-cart-outline text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">{wishlistCount}</h4><p>Wish List</p></div>
                            </div>
                          </div>
                          <div className="col-xl-4">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle mr-3 bg-success"><i className="mdi mdi-ticket-percent text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">02</h4><p>Voucher</p></div>
                            </div>
                          </div>
                          <div className="col-xl-4 mt-3">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle mr-3 bg-info"><i className="mdi mdi-wallet-outline text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">{walletTotal === null ? '-' : walletTotal}</h4><p>Wallet Total</p></div>
                            </div>
                          </div>
                          <div className="col-xl-4 mt-3">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle mr-3 bg-secondary"><i className="mdi mdi-cash-multiple text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">{earnings === null ? '-' : earnings}</h4><p>Earnings</p></div>
                            </div>
                          </div>
                        </div>
                        {/* Removed extra cards; data moved under Contact Information for better visibility */}
                      </div>
                    </div>
                    <div className={`tab-pane fade ${activeTab === 'products' ? 'show active' : ''}`} id="products" role="tabpanel" aria-labelledby="products-tab">
                      <div className="tab-widget mt-5">
                        <div className="row">
                          <div className="col-xl-4">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle mr-3 bg-primary"><i className="mdi mdi-package-variant text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">{grouped?.totalProduct ?? '-'}</h4><p>Total Products</p></div>
                            </div>
                          </div>
                          <div className="col-xl-4">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle mr-3 bg-info"><i className="mdi mdi-shape-outline text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">{Array.isArray(grouped?.statByCategory) ? grouped.statByCategory.length : '-'}</h4><p>Categories</p></div>
                            </div>
                          </div>
                          <div className="col-xl-4">
                            <div className="media widget-media p-3 bg-white border">
                              <div className="icon rounded-circle mr-3 bg-warning"><i className="mdi mdi-earth text-white "></i></div>
                              <div className="media-body align-self-center"><h4 className="text-primary mb-2">{Array.isArray(grouped?.statByCommunity) ? grouped.statByCommunity.length : '-'}</h4><p>Communities</p></div>
                            </div>
                          </div>
                        </div>
                        {/* Grouped Products by Category */}
                        <div className="mt-4">
                          <h5 className="text-dark">Products by Category</h5>
                          {Array.isArray(grouped?.statByCategory) && grouped.statByCategory.length > 0 ? (
                            <div className="row">
                              {grouped.statByCategory.map((c) => (
                                <div key={c.categoryId || c.categoryName} className="col-md-6 mb-3">
                                  <div className="border rounded p-3 d-flex align-items-center">
                                    {c.categoryIcon ? <img src={c.categoryIcon} alt={c.categoryName} style={{ width: 28, height: 28, objectFit: 'cover', marginRight: 10 }} /> : null}
                                    <div className="flex-fill">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-dark">{c.categoryName || 'Uncategorized'}</span>
                                        <span className="badge bg-light text-dark">{c.totalProducts}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mb-0">No category stats</p>
                          )}
                        </div>
                        {/* Grouped Products by Community */}
                        <div className="mt-4">
                          <h5 className="text-dark">Products by Community</h5>
                          {Array.isArray(grouped?.statByCommunity) && grouped.statByCommunity.length > 0 ? (
                            <div className="row">
                              {grouped.statByCommunity.map((c) => (
                                <div key={c.communityId || c.communityName} className="col-md-6 mb-3">
                                  <div className="border rounded p-3 d-flex align-items-center">
                                    {c.communityIcon ? <img src={c.communityIcon} alt={c.communityName} style={{ width: 28, height: 28, objectFit: 'cover', marginRight: 10 }} /> : null}
                                    <div className="flex-fill">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-dark">{c.communityName || 'Unassigned'}</span>
                                        <span className="badge bg-light text-dark">{c.totalProducts}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mb-0">No community stats</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={`tab-pane fade ${activeTab === 'settings' ? 'show active' : ''}`} id="settings" role="tabpanel" aria-labelledby="settings-tab">
                      <div className="tab-pane-content mt-5">
                        <form onSubmit={(e) => e.preventDefault()}>
                          <div className="row mb-2">
                            <div className="col-lg-6"><div className="form-group"><label htmlFor="firstName">First name</label><input type="text" className="form-control" id="firstName" defaultValue={user.name.split(' ')[0]} /></div></div>
                            <div className="col-lg-6"><div className="form-group"><label htmlFor="lastName">Last name</label><input type="text" className="form-control" id="lastName" defaultValue={user.name.split(' ').slice(1).join(' ')} /></div></div>
                          </div>
                          <div className="form-group mb-4"><label htmlFor="userName">User name</label><input type="text" className="form-control" id="userName" defaultValue={user.name.replace(/\s+/g, '.').toLowerCase()} /><span className="d-block mt-1">Update your profile information.</span></div>
                          <div className="form-group mb-4"><label htmlFor="email">Email</label><input type="email" className="form-control" id="email" defaultValue={user.email} /></div>
                          <div className="d-flex justify-content-end mt-5"><button type="submit" className="btn btn-primary mb-2 btn-pill">Update Profile</button></div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SellerProfile;


